import React, { useState } from "react";
import { API, LOGO_URL, staffApi } from "@/lib/api";
import { PageBackground } from "@/components/PageBackground";
import { Talbiyah, GLASS, SHELL } from "@/components/Talbiyah";
import { TelecallerSheet } from "@/components/telecaller/TelecallerSheet";
import { AccountsView } from "@/components/AccountsView";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { IdCard, Loader2, LogOut, IndianRupee } from "lucide-react";

export default function StaffLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [staff, setStaff] = useState(() => { try { return JSON.parse(localStorage.getItem("staff_profile")); } catch { return null; } });
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await fetch(`${API}/staff/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Login failed");
      setStaff(d.staff);
      localStorage.setItem("staff_token", d.token);
      localStorage.setItem("staff_profile", JSON.stringify(d.staff));
      toast.success(`Welcome, ${d.staff.name}`);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem("staff_token"); localStorage.removeItem("staff_profile"); setStaff(null); };

  if (staff) {
    return (
      <div className="relative min-h-screen p-4 sm:p-6 space-y-5" data-testid="staff-dashboard">
        <PageBackground />
        <Card className="p-4 bg-card/40 border-gold/30 flex flex-wrap items-center gap-4">
          <img src={LOGO_URL} alt="logo" className="w-12 h-12 rounded-full ring-1 ring-gold/50" />
          <div className="mr-auto">
            <p className="font-serif text-lg font-semibold text-white">{staff.name}</p>
            {staff.designation && <p className="text-xs text-gold">{staff.designation}{staff.office ? ` · ${staff.office}` : ""}</p>}
          </div>
          <div className="bg-primary/40 rounded-lg px-3 py-2"><p className="eyebrow text-muted-foreground">Monthly Salary</p><p className="font-serif text-gold flex items-center"><IndianRupee className="w-4 h-4" />{(staff.salary || 0).toLocaleString("en-IN")}</p></div>
          <div className="bg-primary/40 rounded-lg px-3 py-2"><p className="eyebrow text-muted-foreground">Commission</p><p className="font-serif text-gold flex items-center"><IndianRupee className="w-4 h-4" />{(staff.commission || 0).toLocaleString("en-IN")}</p></div>
          <Button data-testid="staff-logout-btn" onClick={logout} variant="outline" className="border-gold/40 text-gold gap-2"><LogOut className="w-4 h-4" /> Sign out</Button>
        </Card>
        {staff.is_accountant && (
          <div>
            <p className="font-serif text-xl text-white mb-3">Accounts</p>
            <AccountsView client={staffApi} />
          </div>
        )}
        <div>
          <p className="font-serif text-xl text-white mb-3">Telecaller Sheet</p>
          <TelecallerSheet client={staffApi} token={localStorage.getItem("staff_token")} />
        </div>
      </div>
    );
  }

  return (
    <div className={SHELL} data-testid="staff-login-page">
      <PageBackground dim={0.04} />
      <Talbiyah />
      <div className={`w-full max-w-[380px] fade-in ${GLASS}`}>
        <div className="text-center mb-5">
          <img src={LOGO_URL} alt="logo" className="w-14 h-14 rounded-full ring-1 ring-gold/50 mx-auto" />
          <p className="font-serif text-lg font-semibold text-white mt-3 flex items-center justify-center gap-2"><IdCard className="w-4 h-4 text-gold" /> Staff Login</p>
          <p className="text-xs text-muted-foreground mt-1">Bengal Umrah House — team members</p>
        </div>
        <form onSubmit={login} className="space-y-3">
          <div><Label className="text-xs text-muted-foreground">Login email</Label><Input data-testid="staff-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-card/60" required /></div>
          <div><Label className="text-xs text-muted-foreground">Password</Label><Input data-testid="staff-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 bg-card/60" required /></div>
          <Button type="submit" data-testid="staff-login-btn" disabled={loading} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}</Button>
        </form>
      </div>
    </div>
  );
}
